import { Component, computed, DestroyRef, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { FormGroup, FormControl, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DynamicRouteHandlingService, GenericDropdownComponent, InputFieldComponent } from '@zayan-erp/shared';
import { signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SaleTaxClassService, SalesTaxClassDto } from '../../services/sale-tax-class.service';
import { TaxCategoriesService } from '../../services/tax-categories.service';
import { TaxAuthorityService } from '../../services/tax-authority.service';
import { tap } from 'rxjs';
import { of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-sales-tax-classes-form',
  imports: [InputFieldComponent, FormsModule, ReactiveFormsModule, GenericDropdownComponent],
  templateUrl: './sales-tax-classes-form.component.html'
})
export class SalesTaxClassesFormComponent {
  #activatedRoute = inject(ActivatedRoute);
  #saleTaxClassService = inject(SaleTaxClassService);
  #taxCategoriesService = inject(TaxCategoriesService);
  #taxAuthorityService = inject(TaxAuthorityService);
  #router = inject(Router);
  #dynamicRouteHandling = inject(DynamicRouteHandlingService);
  #destroyRef = inject(DestroyRef);

  constructor() {
    this.#dynamicRouteHandling.submitButton.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.submit());
  }

  selectedSalesTaxClassId = signal<string | null>(this.#activatedRoute.snapshot.paramMap.get('id'));
  editRxResource = rxResourceFixed({
    params: () => ({ id: this.selectedSalesTaxClassId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.#saleTaxClassService.getSalesTaxClassById(params.id).pipe(
          tap(salesTaxClass => {
            this.editForm(salesTaxClass);
          })
        );
      }
      return of(null);
    }
  });

  editForm(salesTaxClass: SalesTaxClassDto) {
    const taxAuthorityIds = salesTaxClass.authorities?.map(authority => authority.id) || [];
    this.salesTaxClassForm.patchValue({
      code: salesTaxClass.code,
      description: salesTaxClass.description || '',
      taxCategoryId: salesTaxClass.taxCategoryId,
      taxAuthorityIds: taxAuthorityIds
    });
  }

  salesTaxClassForm = new FormGroup({
    code: new FormControl<string | null>(null, [Validators.required]),
    description: new FormControl<string | null>(null, [Validators.required]),
    taxCategoryId: new FormControl<string | null>(
      this.#activatedRoute.snapshot.queryParamMap.get('taxCategoryId') ?? null,
      [Validators.required]
    ),
    taxAuthorityIds: new FormControl<string[]>([], [Validators.required, Validators.maxLength(3)])
  });

  taxCategories = toSignal(this.#taxCategoriesService.readTaxCategories(), { initialValue: [] });
  taxCategoriesList = computed(() =>
    this.taxCategories().map(category => ({
      id: category.id,
      firstLabel: category.description || category.code,
      secondLabel: category.code
    }))
  );

  taxAuthorities = toSignal(this.#taxAuthorityService.readTaxAuthorities(), { initialValue: [] });
  taxAuthoritiesList = computed(() =>
    this.taxAuthorities().map(authority => ({
      id: authority.id,
      firstLabel: authority.name,
      secondLabel: authority.code
    }))
  );

  submit() {
    if (this.salesTaxClassForm.invalid) {
      this.salesTaxClassForm.markAllAsTouched();
      return;
    }

    const formValue = this.salesTaxClassForm.value;
    const payload = {
      code: formValue.code!,
      description: formValue.description!,
      taxCategoryId: formValue.taxCategoryId!,
      taxAuthorityIds: formValue.taxAuthorityIds || []
    };

    if (this.selectedSalesTaxClassId()) {
      this.#saleTaxClassService.updateSalesTaxClass(this.selectedSalesTaxClassId()!, payload).subscribe({
        next: () => this.afterSubmitSuccess()
      });
    } else {
      this.#saleTaxClassService.createSalesTaxClass(payload).subscribe({
        next: () => this.afterSubmitSuccess()
      });
    }
  }

  afterSubmitSuccess() {
    this.salesTaxClassForm.reset();
    this.#router.navigate([this.#activatedRoute.snapshot.data['returnRoute']], { queryParamsHandling: 'preserve' });
    this.#dynamicRouteHandling.reload.next(!this.#dynamicRouteHandling.reload.value);
  }
}
